import type { Granularity } from "./granularity";

/**
 * FONTE ÚNICA dos chartKeys do seletor Mês/Semana/Dia (contrato 11/10). O BE lê `g_<key>` nos endpoints de aba
 * e `granularity` nos de série única. Para renomear uma chave, troque só o `key` aqui.
 * `default`: granularidade sem escolha do usuário — fixa (desenho de hoje) ou "byPeriod" (≤31 d Dia, ≤92 d Semana, senão Mês).
 * `legacyParam`: parâmetro antigo da URL ainda aceito na leitura (nunca mais escrito).
 * `api`: "tab" = `g_<key>`; "single" = `granularity`; "tab+legacy" = os dois (BE antigo lia `granularity` só para a tendência).
 */
export type ChartDefault = Granularity | "byPeriod";
export interface ChartKeyDef {
  key: string;
  default: ChartDefault;
  api: "tab" | "single" | "tab+legacy";
  endpoint: string;
  legacyParam?: string;
}

const def = <K extends string>(d: ChartKeyDef & { key: K }) => d;

export const CHART_KEYS = {
  /** Visão geral · "Antecipado por … e canal × penetração" (TrendBlock). Efetiva em `trend.granularity`. */
  trend: def({ key: "trend", default: "month", api: "tab+legacy", endpoint: "GET dashboard/overview", legacyParam: "gran" }),
  /** Visão geral · antes × depois do marco (ComparePanel). Dia só com janela ≤ 28 d (2 × janela ≤ 92). Efetiva em `weeklyGranularity`. */
  compareWeekly: def({ key: "compareWeekly", default: "week", api: "tab", endpoint: "GET dashboard/compare" }),
  /** Financeiro · "Receita de taxas e margem por …" (MarginBlock). Semana/Dia usam o período do filtro. Efetiva em `monthlyGranularity`. */
  financialMonthly: def({ key: "financialMonthly", default: "month", api: "tab", endpoint: "GET dashboard/financial" }),
  /** Financeiro › Payback · recebimentos previstos (PaybackCharts). Dia = 56 d à frente; Mês = 3 meses. Efetiva em `weeklyGranularity`. */
  paybackCash: def({ key: "paybackCash", default: "week", api: "tab", endpoint: "GET dashboard/payback" }),
  /** Melhoria contínua · drill do motivo, "Tendência semanal" (ReasonDrill). Efetiva em `weeklyGranularity`. */
  reasonWeekly: def({ key: "reasonWeekly", default: "week", api: "tab", endpoint: "GET improvement/reasons/:id" }),
  /** Melhoria contínua · ponto, "Motivo antes × depois do marco" (BeforeAfterPanel). Efetiva em `series.granularity`. */
  beforeAfter: def({ key: "beforeAfter", default: "week", api: "tab", endpoint: "GET improvement/points/:id" }),
  /**
   * Financeiro · "Receita perdida por demora" (FeeLossFinancialBlock). Janela FIXA até o `to` (12 meses / 16 semanas / 31 dias,
   * `trendRange` do BE): Dia nunca trava pelo período. Efetiva em `feeLoss.series.granularity`.
   * O BE NÃO conhece `g_feeLoss`: o bloco lê o `granularity` global da requisição (aba financial e drill/CSV `dashboard/fee-loss`).
   * Por isso a URL guarda `g_feeLoss` e a API recebe `granularity=<valor>` ("single").
   */
  feeLoss: def({ key: "feeLoss", default: "month", api: "single", endpoint: "GET dashboard/financial (bloco feeLoss) + GET dashboard/fee-loss(.csv)" }),
  /** Precificação · "Mix por faixa no pedido" (MixBlock). Usa o período do filtro. Efetiva em `mix.granularity`. */
  pricingMix: def({ key: "pricingMix", default: "byPeriod", api: "tab", endpoint: "GET dashboard/pricing" }),
  /** Precificação · "Receita de taxas por faixa cobrada" (FeeRevenueBlock). Efetiva em `feeRevenue.granularity`. */
  pricingRevenue: def({ key: "pricingRevenue", default: "byPeriod", api: "tab", endpoint: "GET dashboard/pricing" }),
  /** Precificação · "Taxa efetiva" (EffectiveRateBlock). Efetiva em `effectiveRate.granularity`. */
  pricingRate: def({ key: "pricingRate", default: "byPeriod", api: "tab", endpoint: "GET dashboard/pricing" }),
  /** Visão geral · "Solicitações por dia × calendário" (SeasonalityBlock). Efetiva em `granularity`. */
  seasonality: def({ key: "seasonality", default: "byPeriod", api: "tab", endpoint: "GET dashboard/seasonality" }),
  // SEM seletor (decisão BE 11/10): Projeção e autofinanciamento (mensais por natureza), Pareto e Campanhas (não são série temporal).
} as const;

export type ChartKey = keyof typeof CHART_KEYS;

export const chartParam = (c: Pick<ChartKeyDef, "key">) => `g_${c.key}`;

/** Parâmetros HTTP da granularidade efetiva de um gráfico. */
export function chartApiParams(c: ChartKeyDef, g: Granularity): Record<string, string> {
  if (c.api === "single") return { granularity: g };
  if (c.api === "tab+legacy") return { [chartParam(c)]: g, granularity: g };
  return { [chartParam(c)]: g };
}

/** URL sem as escolhas destes gráficos (voltar ao padrão de vários de uma vez, ex.: depois de um 422). */
export function withoutCharts(search: URLSearchParams, charts: ChartKeyDef[]): URLSearchParams {
  const next = new URLSearchParams(search);
  charts.forEach((c) => {
    next.delete(chartParam(c));
    if (c.legacyParam) next.delete(c.legacyParam);
  });
  return next;
}
